import {Component, OnDestroy, OnInit} from '@angular/core';
import {CartService} from '../../../services/cart/cart.service';
import {SnackService} from '../../../services/snack/snack.service';
import {AuthService} from '../../../services/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {RouteService} from '../../../services/route/route.service';
import {StorageService} from '../../../services/storage/storage.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {
  BookingId,
  Checkout,
  CheckoutFiscal,
  PaymentMethodEnum,
  PaymentTypeEnum,
  PriceInfoEnum,
  PrintTicketType,
  SendType,
  Transaction
} from '../../../generated/sales-terminal-client';
import {cloneDeep} from 'lodash-es';
import * as FileSaver from 'file-saver-es';
import {Subscription, tap} from 'rxjs';
import moment from 'moment';
import {BookingsCreateModalComponent} from '../bookings/create-modal/bookings-create-modal.component';
import {UtilService} from '../../../services/util/util.service';
import {FiscalService} from '../../../services/fiscal/fiscal.service';
import {PeriodPipe} from '../../../pipes/period/period.pipe';
import {CARD, CASH, MAX_TICKETS_IN_EMAIL, ZERO_PRICE} from '../sales-terminal.constants';
import {ConfirmDialogComponent} from '../../../components/confirm-dialog/confirm-dialog.component';
import {GroupTickets} from './cart.constants';
import {NavigationParameters} from './table/cart-table.component';
import {finalize} from 'rxjs/operators';
import {ScrapService} from '../../../services/scrap/scrap.service';
import {Privileges} from '../../admin/privileges/privileges.constants';
import {CartTypes} from '../../../services/cart/cart-base.service';
import {TranslateValuePipe} from '../../../pipes/translateValue/translateValue.pipe';
import {TextInputType} from '../../../components/forms/text-input/text-input.component';
import {CentrePropertyService} from "../../../services/centre-property/centre-property.service";

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit, OnDestroy {
  readonly CASH = CASH;
  readonly CARD = CARD;
  private _subscriptions: Subscription[] = [];
  public ticketType: PrintTicketType;
  public priceInfo: PriceInfoEnum;
  public clientEmail: string;
  public paymentType: number;
  public selfServicePaymentMethod: PaymentMethodEnum;
  public modalIsOpened = false;
  public modalType: string;
  public transaction: Transaction;
  public disabledButtons: boolean;
  public internalComment: string;

  public isLoading = false;
  public bulkMode = false;
  public ticketsPrintedCountBy = 'all';
  public backToSale = true;
  public groupTickets = GroupTickets;
  public groupTicketsValues: GroupTickets[] = Object.values(GroupTickets);
  public Privileges = Privileges;
  public CartTypes = CartTypes;
  public showSelfServiceRelease: boolean = false;

  constructor(
    public cartService: CartService,
    public authService: AuthService,
    public centrePropertyService: CentrePropertyService,
    public utilService: UtilService,
    private dialog: MatDialog,
    private snackService: SnackService,
    private translateService: TranslateService,
    private routeService: RouteService,
    private storageService: StorageService,
    private fiscalService: FiscalService,
    private scrapService: ScrapService,
    private periodPipe: PeriodPipe,
    private translateValuePipe: TranslateValuePipe) {
    this.isLoading = true;
  }

  public ngOnInit(): void {
    this.ticketType = PrintTicketType.Plp;
    this.priceInfo = PriceInfoEnum.Regular;
    if (this.cartService.cartSubscription) {
      this.isLoading = false;
      this.cartService.cartSubscription.add(() => this.updateCart());
    } else {
      this.cartService.cartSubscription = this.cartService.getCart().pipe(
        finalize(() => this.isLoading = false)
      ).subscribe(() => this.updateCart());
      this._subscriptions.push(this.cartService.cartSubscription);
    }
    this._subscriptions.push(
      this.utilService.fiscalOperationFailed.subscribe(() => {
        this.modalIsOpened = false;
        this.modalType = '';
        this.cartService.retryCart();
      })
    );
    this.showSelfServiceRelease = this.cartService.isSelfService && this.cartService.hasSelectedTickets;
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  private updateCart(): void {
    this.cartService.selectAllTickets();
    if (this.authService.checkSalespointProperty('tickets_printed_in_pdf') || this.authService.user.isPromoter) {
      this.ticketType = PrintTicketType.Pdf;
    }
    if (this.routeService.url.includes('create-booking')) {
      this.routeService.replaceUrl('/app/sales-terminal/cart');
      this.openBookingModal();
    }
  }

  public confirmClear(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_cart_clear_confirmation_title'),
        description: this.translateService.instant('general.api_cart_clear_confirmation_description'),
        buttonCancel: this.translateService.instant('general.api_general_cancel'),
        buttonOk: this.translateService.instant('general.api_cart_clear')
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.cartService.emptyCart();
      }
    });
  }

  public get zippedTicketsSent(): boolean {
    return this.cartService.cartTotal.ticketQuantity > MAX_TICKETS_IN_EMAIL;
  }

  public openPaymentModal(paymentType: number): void {
    this.cartService.validateCart({ tickets: this.cartService.getSelectedTicketIds }).subscribe(() => {
      this.paymentType = paymentType;
      this.modalIsOpened = true;
      this.modalType = 'payment';
    });
  }
  public openSelfServicePaymentModal(paymentMethod?: PaymentMethodEnum): void {
    this.selfServicePaymentMethod = paymentMethod;
    this.ticketType = PrintTicketType.Pdf;
    this.modalIsOpened = true;
    this.modalType = 'payment';
  }

  public validateTotalSum(): void {
    if (this.cartService.totalSum < 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '60%',
        panelClass: 'xs-popup',
        data: {
          title: this.translateService.instant('general.api_cart_negative_total_sum_title'),
          description: this.translateService.instant('general.api_cart_negative_total_sum_description'),
          buttonCancel: this.translateService.instant('general.api_cart_negative_total_sum_button_cancel'),
          buttonOk: this.translateService.instant('general.api_cart_negative_total_sum_button_ok')
        }
      });
      dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          this.openPaymentModal(ZERO_PRICE);
        }
      });
    } else {
      this.openPaymentModal(ZERO_PRICE);
    }
  }

  public promoterRelease(): void {
    this.cartService.validateCart({ tickets: this.cartService.getSelectedTicketIds }).subscribe(() => {
      this.paymentType = CASH;
      this.modalIsOpened = true;
      this.modalType = 'confirmRelease';
      this.cartService.updateSubTotals();
    });
  }

  public selfServiceCheckout(toEmail?: boolean): void {
    this.startLoader();
    this.cartService.selfServiceCheckout({
      email: toEmail ? this.clientEmail : null,
      paymentMethod: this.selfServicePaymentMethod
    }).pipe(
      tap({
        finalize: () => toEmail ? this.stopLoader(false) : null,
        error: () => this.stopLoader()
      })
    ).subscribe((transaction: Transaction) => {
      this.transaction = transaction;
      if (toEmail) {
        this.snackService.showSuccess(this.translateService.instant('general.api_cart_self_service_release_succeed'));
        this.onTransactionComplete();
      } else {
        this.printTransaction();
      }
    })
  }

  public printTransaction(startLoader?: boolean): void {
    if (startLoader) {
      this.startLoader();
    }
    this.cartService.printSelfServiceTransaction(this.transaction.id).pipe(
      tap({finalize: () => this.stopLoader(false)})
    ).subscribe(blob => this.printTickets(blob));
  }

  public cancelPayment(): void {
    delete this.paymentType;
    this.modalIsOpened = false;
  }

  public checkoutCart(): void {
    if (this.cartService.isSelfService) {
      this.selfServiceCheckout();
    } else {
      this.startLoader();
      const checkout: Checkout = {
        tickets: this.cartService.getSelectedTicketIds,
        backToSale: this.backToSale,
        paymentType: this.paymentType === CARD ? PaymentTypeEnum.Card : PaymentTypeEnum.Cash,
        clientNotificationEmail: this.cartService.clientNotificationEmail,
        internalComment: this.internalComment
      };
      if (this.cartService.hasSelectedSeasonTickets && this.cartService.webShopCustomer) {
        checkout.webshopUsername = this.cartService.webShopCustomer.userName;
      }
      if (this.authService.checkSalespointProperty('fiscal_procedures_by')) {
        this.fiscalService.checkout(checkout as CheckoutFiscal).pipe(
          tap({error: () => this.stopLoader()})
        ).subscribe(() => {
          this.fiscalService.printTransaction(this.priceInfo).pipe(
            tap({finalize: () => this.stopLoader()})
          ).subscribe((blob) => this.printTickets(blob));
        });
      } else {
        this.cartService.saveTransaction(checkout).pipe(
          tap({error: () => this.stopLoader()})
        ).subscribe((transaction) => {
          this.transaction = transaction;
          this.cartService.printTransaction(this.transaction.id, this.ticketType, this.priceInfo).pipe(
            tap({finalize: () => this.stopLoader()})
          ).subscribe((blob) => this.printTickets(blob));
        });
      }
    }
  }

  private printTickets(blob: Blob): void {
    this.modalType = 'confirmPrint';
    const fileData = UtilService.getFileData('', this.ticketType);
    let file: any;
    if (this.ticketType === PrintTicketType.Plp && fileData.firefox) {
      file = new File([blob], fileData.filename, { type: fileData.mime });
      window.location.href = URL.createObjectURL(file);
    } else {
      file = new Blob([blob], { type: fileData.mime });
      if (this.ticketType === PrintTicketType.Plp) {
        FileSaver.saveAs(file, fileData.filename);
      } else {
        const event = this.cartService.cart.events[0];
        const pdf_filename = this.translateValuePipe.transform(event.title) + '_' + this.periodPipe.transform(event.eventDate);
        if (this.utilService.isMobile) {
          this.utilService.mobileDownload(file, pdf_filename + '.pdf');
        } else {
          this.utilService.previewOrPrintFile(file, null, null, PrintTicketType.Pdf, pdf_filename, this.ticketsPrintedCountBy);
        }
      }
    }
    if (this.authService.checkSalespointProperty('fiscal_procedures_by')) {
      this.fiscalService.getOperationStatus();
    }
    this.priceInfo = PriceInfoEnum.Regular;
    this.cartService.updateClientNotificationEmail();
  }

  public proceedTransaction(): void {
    if (this.cartService.isSelfService) {
      this.selfServiceCheckout(true);
    } else {
      this.startLoader();
      const checkout: Checkout = {
        tickets: this.cartService.getSelectedTicketIds,
        backToSale: this.backToSale,
        paymentType: this.paymentType === CARD ? PaymentTypeEnum.Card : PaymentTypeEnum.Cash,
        clientNotificationEmail: this.cartService.clientNotificationEmail,
        internalComment: this.internalComment
      };
      this.cartService.saveTransaction(checkout).pipe(
        tap({error: () => this.stopLoader()})
      ).subscribe((transaction) => {
        this.transaction = transaction;
        this.cartService.sendTransaction(this.transaction.id, SendType.Client, this.priceInfo, [], { email: this.clientEmail }).pipe(
          tap({
            finalize: () => this.stopLoader(false),
            error: () => this.stopLoader()
          })
        ).subscribe(() => this.finishRelease('general.api_cart_promoter_release_succeed'));
      });
    }
  }

  private onTransactionComplete(): void {
    this.disabledButtons = true;
    const eventIds: number[] = (this.cartService.cart.events || []).map(event => event.id);
    let eventId: number = eventIds.includes(this.cartService.lastEventId) ? this.cartService.lastEventId : (eventIds.length >= 1 ? eventIds[0] : 0);
    const redirectUrl: string = eventId ? ('/app/sales-terminal/search/event/' + eventId) : '/app/sales-terminal';
    this.cartService.getCart().pipe(
      tap({finalize: () => this.disabledButtons = false})
    ).subscribe(() => {
      this.storageService.set(CartService.STORAGE_CART_PURCHASE_SUCCEED, moment.now());
      if (!this.cartService.ticketCount) {
        this.storageService.set(CartService.STORAGE_CART_WINDOW_CLOSE, moment.now());
        if (!UtilService.getBrowser('ff') || !this.authService.checkSalespointProperty('fiscal_procedures_lt')) {
          this.routeService.navigate(redirectUrl);
        }
      } else {
        this.cartService.selectAllTickets();
      }
    });
  }

  public confirmPrinting(): void {
    this.finishRelease();
    if (this.authService.checkSalespointProperty('fiscal_procedures_lt')) {
      this.printFiscal(!this.cartService.ticketCount);
    }
  }

  private printFiscal(redirect: boolean = false): void {
    const redirectUrl = this.cartService.lastEventId ? '/app/sales-terminal/search/event/' + this.cartService.lastEventId : '/app/sales-terminal';
    this.cartService.printFiscal(this.transaction.id).subscribe((blob) => {
      const fileData = UtilService.getFileData('json_fiscal', 'json');
      let file: any;
      if (fileData.firefox) {
        file = new File([blob], fileData.filename, { type: fileData.mime });
        if (redirect) {
          this.routeService.download(URL.createObjectURL(file), redirectUrl);
        } else {
          window.location.href = URL.createObjectURL(file);
        }
      } else {
        file = new Blob([blob], { type: fileData.mime });
        FileSaver.saveAs(file, fileData.filename);
      }
    });
  }

  private finishRelease(successMessage: string = 'general.api_cart_purchase_succeed'): void {
    this.modalType = '';
    this.modalIsOpened = false;
    this.snackService.showSuccess(this.translateService.instant(successMessage));
    this.onTransactionComplete();
  }

  public scrappingProcess(): void {
    const cart = cloneDeep(this.cartService.cart);
    cart.events = [];
    const selectedTicketIds = this.cartService.getSelectedTicketIds;
    this.cartService.cart.events.forEach(event => {
      let selectedEvent = cloneDeep(event);
      selectedEvent.legacyTickets = selectedEvent.legacyTickets.filter(t => selectedTicketIds.includes(t.id));
      if (selectedEvent.legacyTickets?.length) {
        cart.events.push(selectedEvent);
      }
    });
    this.scrapService.cart = cart;
    this.cartService.retryCart();
    this.routeService.navigate('/app/sales-terminal/scrap');
  }

  public openBookingModal(): void {
    const dialogRef: MatDialogRef<BookingsCreateModalComponent, BookingId> = this.dialog.open(BookingsCreateModalComponent, {
      width: '50%',
      data: { ticketIds: this.cartService.getSelectedTicketIds }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cartService.retryCart();
        this.routeService.navigate('/app/sales-terminal/bookings/view/' + result.id);
      }
    });
  }

  public navigateToEventView(navParams: NavigationParameters): void {
    this.routeService.navigate('/app/sales-terminal/search/event/' + navParams.eventId);
  }

  public toggleBulkMode(): void {
    this.bulkMode = !this.bulkMode;
    if (!this.bulkMode) {
      this.cartService.groupTicketsBy = GroupTickets.None;
    } else if (this.cartService.groupTicketsBy === GroupTickets.None && this.cartService.cartType !== CartTypes.SelfService) {
      this.cartService.groupTicketsBy = GroupTickets.PriceType;
    }
  }

  public get canCreateBooking(): boolean {
    return this.authService.checkPrivileges(Privileges.BOOKING_CREATE) && !this.cartService.hasSelectedSeasonTickets && this.cartService.cartType !== CartTypes.SelfService;
  }

  public get showFooter(): boolean {
    return !this.modalIsOpened && (this.authService.checkPrivileges(Privileges.PAYMENT_OPTIONS) ||
      ((this.authService.user.isPromoter || this.cartService.isSelfService) && this.cartService.hasSelectedTickets));
  }

  private startLoader() {
    this.disabledButtons = true;
    this.isLoading = true;
  }
  private stopLoader(enableButtons = true) {
    this.disabledButtons = !enableButtons;
    this.isLoading = false;
  }

  protected readonly TextInputType = TextInputType;
  protected readonly PaymentMethodEnum = PaymentMethodEnum;
}
